import { useCallback, useEffect, useState } from 'react';
import { Loader2, Link as LinkIcon, ShieldCheck, Ban, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { documentCaptureService } from '../../services/documentCaptureService';
import { useReviewDecision } from '../../hooks/useReviewDecision';
import { ManualLinkCard } from './ManualLinkCard';
import { DocReviewReason, type NsPreview, type ReviewQueueItem } from '../../types/documentCapture.types';

/**
 * NetSuite-tab action forms (86d3rrfdk). One panel, three shapes driven by the item's reason:
 *  - **Conflict** — the NS field holds an unknown value → Use DOP link / Keep existing.
 *  - **Voided** — the transaction is voided → Reject (withdraw) / Keep watching. No "Link anyway".
 *  - **Multiple matches** — the pick-to-link picker: candidates re-queried LIVE, never persisted.
 *  - Everything else here (closed period, link failed) → informational + Reject.
 */
interface NsReviewPanelProps {
  item: ReviewQueueItem;
}

function pesos(amount: number | null): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
}

export function NsReviewPanel({ item }: NsReviewPanelProps) {
  return (
    <div className="space-y-4">
      {/*
        86d45qb9x — OUTSIDE the reason branches on purpose. Every shape on this tab is a link-lane
        item, so every one of them is document-anchored and hits the same read path; putting this
        inside one branch would leave the conflict and multi-match reviewers with no way to the paper.

        It is not a fallback for a broken pane either. The overlay renders page 1 and only page 1, so
        for a multi-page voucher — an OP with the amount on page 2 — this is the ONLY route to the
        rest of the document, whether or not the pane works.
      */}
      <OpenOriginalPdf item={item} />
      <NsReviewActions item={item} />
    </div>
  );
}

/**
 * The original PDF, all pages, in the browser's own viewer.
 *
 * Mirrors the Scans tab's control (`ScansReviewPanel`) deliberately — same variant, same icon, same
 * verb — because it is the same action and a reviewer moves between the two tabs.
 *
 * 🚨 `onClick`, never `<Button asChild>` wrapping an anchor: this project's Button renders
 * `{isLoading && <Loader2/>}` beside `{children}`, so Slot receives two children and React throws
 * "React.Children.only expected to receive a single React element child" — which blanks the whole
 * page while passing tsc, lint, vitest and xUnit. That is exactly how 86d45ffw4 shipped a crash.
 */
function OpenOriginalPdf({ item }: { item: ReviewQueueItem }) {
  const [opening, setOpening] = useState(false);

  const open = useCallback(async () => {
    setOpening(true);
    try {
      await documentCaptureService.openReviewItemPdf(item.id);
    } catch {
      toast.error('Couldn’t open the PDF — try again in a moment.');
    } finally {
      setOpening(false);
    }
  }, [item.id]);

  return (
    <Button variant="outline" size="sm" onClick={() => void open()} disabled={opening}>
      {opening ? (
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <FileText className="mr-1.5 h-4 w-4" aria-hidden />
      )}
      Open the PDF
      {/* The count is why the button matters on a voucher, so it is on the button, not in a tooltip.
          It reads correctly only because the queue projection now falls back to the document's active
          file for document-anchored items — before 86d45qb9x this was 0 on every item here. */}
      {item.pageCount > 1 && (
        <span className="ml-1 text-muted-foreground">· {item.pageCount} pages</span>
      )}
    </Button>
  );
}

function NsReviewActions({ item }: NsReviewPanelProps) {
  const { submit, submitting } = useReviewDecision();
  const epoch = item.linkEpoch ?? 0;

  if (item.reason === DocReviewReason.MultipleNsMatches) {
    return <MultiMatchPicker item={item} />;
  }

  // 14ykpqtwauu's AppliedCredits branch lived here until 14ykpqtx1z9 retired the hold. A resolved item
  // of that reason renders through the generic shape below if anyone opens it from history.

  if (item.reason === DocReviewReason.CustbodyConflict) {
    return (
      <div className="space-y-4">
        {item.previousCustbodyValue && (
          <div className="rounded-lg border border-warning/25 bg-warning/5 p-3 text-sm">
            <p className="font-medium text-foreground">NetSuite already holds a value we didn’t set:</p>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{item.previousCustbodyValue}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={submitting}
            onClick={() =>
              void submit(() => documentCaptureService.resolveConflict(item.id, true, epoch), {
                successMessage: 'Linked with the DOP viewer link',
              })
            }
          >
            <LinkIcon className="mr-1.5 h-4 w-4" aria-hidden />
            Use DOP link
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={submitting}
            onClick={() =>
              void submit(() => documentCaptureService.resolveConflict(item.id, false, epoch), {
                successMessage: 'Kept the existing NetSuite value',
              })
            }
          >
            <ShieldCheck className="mr-1.5 h-4 w-4" aria-hidden />
            Keep existing
          </Button>
        </div>
      </div>
    );
  }

  /*
    86d45ffw4 — voided / closed period / refused PATCH all land here, and all get the same shape:
    what NetSuite said (folded away), the manual-link card, then Reject.

    The reason sentence is NOT repeated here. ReviewOverlay renders it verbatim in the muted box
    directly above every panel (:530), so printing `item.reasonDetail` again put the same sentence on
    screen twice, sixty pixels apart.

    There is no "Keep watching" button on this tab any more (owner decision, 2026-08-27). Nothing
    retries these: DocScanLinkHandler returns Success() on a closed period and on a refused PATCH, and
    treats both as terminal no-ops afterwards. The button was the only thing that re-armed the lane,
    and with the manual link available it is no longer the answer to any of these three. Keep watching
    still exists on the Scans tab, where "never appeared in NetSuite" genuinely is worth re-checking.
  */
  /*
    The NetSuite tab also carries LinkBroken and WrongRecordFamily, and NEITHER can be fixed by
    pasting a link: the first had its link deliberately cleared, the second is filed against the wrong
    record family. ConfirmManualLinkAsync refuses both, so offering the card here would put a button
    on screen whose only outcome is a refusal toast. Gate the panel on the same three reasons the
    service accepts — the UI and the server must agree about what this action is for.
  */
  const canLinkByHand =
    item.reason === DocReviewReason.ClosedPeriod ||
    item.reason === DocReviewReason.LinkFailed ||
    item.reason === DocReviewReason.NsVoided ||
    item.reason === DocReviewReason.LinkRefused;

  /*
    Is DOP still going to have another go at this by itself?

    Only LinkFailed is — it is the reason the write consumer raises while it retries a collision or a
    refusal it could not classify. ClosedPeriod, NsVoided and LinkRefused are all handed over
    deliberately: the queue has stopped, because retrying cannot change any of those answers
    (owner ruling 2026-09-11).
  */
  const systemStillRetrying = item.reason === DocReviewReason.LinkFailed;

  return (
    <div className="space-y-4">
      <NsRawResponse item={item} />
      {canLinkByHand && (
        <>
          {/*
            86d4a0zhr — this sentence has been wrong in BOTH directions, which is why it now reads off
            `systemStillRetrying` rather than off a hand-listed reason.

            It first said "DOP won't try again" for everything, which was true only while the link lane
            reported an unclassified refusal as a finished job. The write consumer made most refusals
            retry, so that sentence started telling reviewers the opposite of what was happening and
            pushed them into hand-linking work the system was about to do itself. Inverting it to a
            ClosedPeriod check then broke the other way the moment Validation and Voided became
            handoffs: those reviewers were told to wait for a retry that was never coming.

            Both branches still offer the manual link — a reviewer who wants it done now should not
            have to wait for a retry that might succeed.
          */}
          <p className="text-sm text-muted-foreground">
            {systemStillRetrying
              ? 'DOP is still retrying this. You can link it yourself now, or wait.'
              : 'DOP won’t try again — link it yourself or reject it.'}
          </p>
          <ManualLinkCard item={item} />
        </>
      )}
      <RejectBox item={item} />
    </div>
  );
}

/**
 * What NetSuite actually sent, folded away. Until 86d45ffw4 this text WAS the reason banner, so a
 * reviewer deciding what to do was reading an RFC-9110 error envelope to find out a period was
 * closed. Items raised before that migration have no stored raw text — their sentence is still the
 * raw message — so this renders nothing rather than an empty disclosure.
 */
function NsRawResponse({ item }: { item: ReviewQueueItem }) {
  const [open, setOpen] = useState(false);
  if (!item.nsRawResponse) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex h-6 items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight className={cn('h-3 w-3 transition-transform', open && 'rotate-90')} aria-hidden />
        {open ? 'Hide what NetSuite sent back' : 'What NetSuite sent back'}
      </button>
      {open && (
        <p className="mt-1.5 break-words rounded-md bg-muted/60 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {item.nsRawResponse}
        </p>
      )}
    </div>
  );
}

/** Reject-with-reason — the other answer to a NetSuite refusal, beside linking it by hand. */
function RejectBox({ item }: { item: ReviewQueueItem }) {
  const { submit, submitting } = useReviewDecision();
  const [reason, setReason] = useState('');
  const epoch = item.linkEpoch ?? 0;

  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-lg border border-destructive/25 bg-destructive/5 p-3">
        <label htmlFor="ns-reject-reason" className="block text-sm font-medium text-foreground">
          Reject this document <span className="text-destructive">*</span>
        </label>
        <Textarea
          id="ns-reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Transaction voided in NetSuite; scan no longer needed."
          rows={2}
        />
        <Button
          variant="destructive"
          size="sm"
          disabled={!reason.trim() || submitting}
          onClick={() =>
            void submit(() => documentCaptureService.reject(item.id, reason.trim(), epoch), {
              successMessage: 'Document rejected',
            })
          }
        >
          <Ban className="mr-1.5 h-4 w-4" aria-hidden />
          Reject
        </Button>
      </div>
    </div>
  );
}

/** Multiple-match pick-to-link: candidates re-queried LIVE at review time, never from a snapshot. */
function MultiMatchPicker({ item }: { item: ReviewQueueItem }) {
  const { submit, submitting } = useReviewDecision();
  const [preview, setPreview] = useState<NsPreview | null>(null);
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading');
  const [chosen, setChosen] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!item.docNumber) return;
    setState('loading');
    try {
      const result = await documentCaptureService.previewNetSuite(item.docNumber);
      setPreview(result);
      setState('done');
    } catch {
      setState('error');
    }
  }, [item.docNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const link = useCallback(() => {
    if (!chosen) return;
    void submit(() => documentCaptureService.resolveMultiMatch(item.id, chosen, item.linkEpoch ?? 0), {
      successMessage: 'Linked to the chosen transaction',
    });
  }, [submit, item.id, item.linkEpoch, chosen]);

  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading candidate transactions…
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">Couldn’t load the candidates from NetSuite.</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {item.docNumber} matches {preview?.count ?? 0} NetSuite transactions. Pick the one this scan belongs to.
      </p>
      <ul className="space-y-2">
        {preview?.matches.map((match) => (
          <li key={match.internalId}>
            <button
              type="button"
              onClick={() => setChosen(match.internalId)}
              disabled={match.voided}
              aria-pressed={chosen === match.internalId}
              className={cn(
                'w-full rounded-lg border p-2.5 text-left text-sm transition-colors',
                match.voided
                  ? 'cursor-not-allowed border-border bg-muted opacity-60'
                  : chosen === match.internalId
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-accent'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{match.entityName ?? '—'}</span>
                {match.voided && <span className="text-xs text-warning">voided</span>}
              </div>
              <div className="tabular-nums text-muted-foreground">
                {match.tranDate ?? '—'} · {pesos(match.amount)}
              </div>
            </button>
          </li>
        ))}
      </ul>
      <Button onClick={link} disabled={!chosen || submitting} size="sm">
        {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : <LinkIcon className="mr-1.5 h-4 w-4" aria-hidden />}
        Link to this transaction
      </Button>
    </div>
  );
}
